<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import {
    message,
    Button,
    Card,
    Tag,
    Drawer,
    Spin,
    Space
} from 'ant-design-vue';
import { LeftOutlined, RightOutlined, ArrowLeftOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { getConsultantSchedule } from '@/api/consultation/consultationApi';
import { usePermission } from '@/composables/usePermission';
import { isWeekendBlackoutDay } from '@/utils/consultantScheduleMap';
import ScheduleMinSlotsNotice from '@/components/consultation/ScheduleMinSlotsNotice.vue';

const route = useRoute();
const router = useRouter();
const { canView, canUpdate } = usePermission('ConsultantSchedule');

const coreMemberId = computed(() => String(route.params.coreMemberId || ''));

const loading = ref(false);
const consultant = ref(null);
const days = ref([]);
const selectedMonth = ref(
    typeof route.query.month === 'string' && /^\d{4}-\d{2}$/.test(route.query.month)
        ? route.query.month
        : dayjs().format('YYYY-MM')
);
const drawerOpen = ref(false);
const selectedDay = ref(null);
const minMonthlyOpenSlots = ref(0);

const WEEKDAY_HEADERS = ['日', '一', '二', '三', '四', '五', '六'];

const monthLabel = computed(() =>
    dayjs(`${selectedMonth.value}-01`).format('YYYY年M月')
);

const monthRange = computed(() => {
    const start = dayjs(`${selectedMonth.value}-01`).startOf('month');
    const end = start.endOf('month');
    return {
        from: start.format('YYYY-MM-DD'),
        to: end.format('YYYY-MM-DD')
    };
});

const daysByDate = computed(() =>
    Object.fromEntries((days.value || []).map((d) => [d.date, d]))
);

const monthOpenCount = computed(() =>
    (days.value || []).reduce((sum, d) => sum + (d.summary?.open ?? 0), 0)
);

/** 月曆格：含月初空白，7 欄 */
const calendarCells = computed(() => {
    const start = dayjs(`${selectedMonth.value}-01`);
    const daysInMonth = start.daysInMonth();
    const leading = start.day();
    const cells = [];

    for (let i = 0; i < leading; i++) {
        cells.push({ empty: true, key: `pad-${i}` });
    }

    for (let d = 1; d <= daysInMonth; d++) {
        const day = start.date(d);
        const date = day.format('YYYY-MM-DD');
        const dayData = daysByDate.value[date];
        const blocked = isWeekendBlackoutDay(day);
        cells.push({
            empty: false,
            key: date,
            date,
            day: d,
            isToday: date === dayjs().format('YYYY-MM-DD'),
            blocked,
            summary: dayData?.summary ?? { open: 0, booked: 0, unavailable: 0 },
            slots: dayData?.slots ?? []
        });
    }

    return cells;
});

const versionLabel = (record) =>
    record?.version_name || (record?.version_id ? `版本 #${record.version_id}` : '—');

const versionEditorTo = (versionId) => ({
    name: 'FinanceVersionView',
    params: { id: String(versionId) },
    query: { readonly: '1' }
});

const modeLabel = (mode) => (mode === 'offline' ? '實體' : mode === 'online' ? '線上' : mode || '—');

const kindLabel = (kind) => {
    const map = {
        open: '開放',
        booked: '已預約',
        unavailable: '不可排'
    };
    return map[kind] || kind;
};

const kindTagColor = (kind) => {
    const map = {
        open: 'success',
        booked: 'processing',
        unavailable: 'default'
    };
    return map[kind] || 'default';
};

const shiftMonth = (delta) => {
    selectedMonth.value = dayjs(`${selectedMonth.value}-01`)
        .add(delta, 'month')
        .format('YYYY-MM');
};

const goTodayMonth = () => {
    selectedMonth.value = dayjs().format('YYYY-MM');
};

const isCurrentMonth = computed(
    () => selectedMonth.value === dayjs().format('YYYY-MM')
);

const openDayDrawer = (cell) => {
    if (cell.empty || cell.blocked) return;
    selectedDay.value = daysByDate.value[cell.date] || {
        date: cell.date,
        summary: cell.summary,
        slots: cell.slots
    };
    drawerOpen.value = true;
};

const load = async () => {
    if (!coreMemberId.value || !canView.value) return;
    loading.value = true;
    try {
        const { from, to } = monthRange.value;
        const res = await getConsultantSchedule(coreMemberId.value, { from, to });
        if (res.status !== 1) throw new Error(res.message || '載入失敗');
        consultant.value = res.data?.consultant ?? null;
        days.value = res.data?.days ?? [];
        minMonthlyOpenSlots.value = Number(res.data?.min_monthly_open_slots) || 0;
    } catch (e) {
        message.error(e.message || '載入排班失敗');
    } finally {
        loading.value = false;
    }
};

const goBack = () => {
    router.push({ name: 'Consultants' });
};

const goEditSchedule = () => {
    router.push({
        name: 'ConsultantScheduleEdit',
        params: { coreMemberId: coreMemberId.value },
        query: { month: selectedMonth.value }
    });
};

watch([selectedMonth, coreMemberId], () => {
    load();
});

onMounted(load);
</script>

<template>
    <div class="consultant-schedule pb-10">
        <p v-if="!canView" class="text-slate-500">無檢視權限</p>

        <template v-else>
            <div class="schedule-page-bar mb-4">
                <Button type="text" class="schedule-page-bar__back" @click="goBack">
                    <template #icon><ArrowLeftOutlined /></template>
                    返回諮詢師列表
                </Button>
                <h1 class="schedule-page-bar__title">諮詢師排班</h1>
            </div>

            <Spin :spinning="loading">
                <Card
                    v-if="consultant"
                    :bordered="false"
                    class="admin-panel mb-4 shadow-md rounded-2xl border border-[#ebe4d6] bg-white"
                >
                    <div class="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <h2 class="m-0 text-xl font-semibold text-slate-800">
                                {{ consultant.display_name || consultant.core_member_id }}
                            </h2>
                            <p class="mt-1 mb-0 text-sm text-slate-500">
                                等級：{{ consultant.level_name || consultant.level || '—' }}
                                <span class="mx-2">·</span>
                                狀態：
                                <Tag :color="consultant.active ? 'success' : 'default'">
                                    {{ consultant.active ? '啟用' : '停用' }}
                                </Tag>
                            </p>
                        </div>
                        <Space wrap class="admin-table-actions">
                            <Button
                                v-if="canUpdate"
                                type="primary"
                                ghost
                                size="small"
                                class="admin-table-actions__btn"
                                @click="goEditSchedule"
                            >
                                代排班表
                            </Button>
                        </Space>
                    </div>
                    <ScheduleMinSlotsNotice
                        class="mt-3"
                        :min="minMonthlyOpenSlots"
                        :current="monthOpenCount"
                        variant="view"
                    />
                </Card>

                <Card
                    :bordered="false"
                    class="admin-panel shadow-md rounded-2xl border border-[#ebe4d6] bg-white overflow-hidden"
                >
                    <div class="schedule-month-nav">
                        <div class="schedule-month-nav__pager">
                            <Button
                                type="text"
                                class="schedule-month-nav__arrow"
                                aria-label="上個月"
                                @click="shiftMonth(-1)"
                            >
                                <template #icon><LeftOutlined /></template>
                            </Button>
                            <span class="schedule-month-nav__label">
                                {{ monthLabel }}
                            </span>
                            <Button
                                type="text"
                                class="schedule-month-nav__arrow"
                                aria-label="下個月"
                                @click="shiftMonth(1)"
                            >
                                <template #icon><RightOutlined /></template>
                            </Button>
                        </div>
                        <Button
                            size="small"
                            class="schedule-month-nav__today"
                            :class="{ 'is-current': isCurrentMonth }"
                            :disabled="isCurrentMonth"
                            @click="goTodayMonth"
                        >
                            本月
                        </Button>
                    </div>

                    <div class="schedule-calendar">
                        <div class="schedule-calendar__head">
                            <div
                                v-for="(w, i) in WEEKDAY_HEADERS"
                                :key="w"
                                class="schedule-calendar__weekday"
                                :class="{ 'is-weekend': i === 0 || i === 6 }"
                            >
                                {{ w }}
                            </div>
                        </div>
                        <div class="schedule-calendar__grid">
                            <template v-for="cell in calendarCells" :key="cell.key">
                                <div
                                    v-if="cell.empty"
                                    class="schedule-calendar__cell schedule-calendar__cell--empty"
                                />
                                <button
                                    v-else
                                    type="button"
                                    class="schedule-calendar__cell schedule-calendar__cell--day"
                                    :class="{
                                        'is-today': cell.isToday,
                                        'is-blocked': cell.blocked
                                    }"
                                    :disabled="cell.blocked"
                                    @click="openDayDrawer(cell)"
                                >
                                    <span class="schedule-calendar__date">{{ cell.day }}</span>
                                    <div class="schedule-calendar__pills">
                                        <template v-if="!cell.blocked">
                                            <span
                                                v-if="cell.summary.open > 0"
                                                class="pill pill--open"
                                                :title="`開放 ${cell.summary.open}`"
                                            >
                                                開放 {{ cell.summary.open }}
                                            </span>
                                            <span
                                                v-if="cell.summary.unavailable > 0"
                                                class="pill pill--off"
                                                :title="`不可排 ${cell.summary.unavailable}`"
                                            >
                                                不可 {{ cell.summary.unavailable }}
                                            </span>
                                        </template>
                                        <span
                                            v-if="cell.summary.booked > 0"
                                            class="pill pill--booked"
                                            :title="`已預約 ${cell.summary.booked}`"
                                        >
                                            已約 {{ cell.summary.booked }}
                                        </span>
                                    </div>
                                </button>
                            </template>
                        </div>
                    </div>

                </Card>
            </Spin>

            <Drawer
                v-model:open="drawerOpen"
                :title="
                    selectedDay
                        ? dayjs(selectedDay.date).format('YYYY年M月D日') + ' 時段'
                        : '時段'
                "
                placement="right"
                width="400"
            >
                <template v-if="selectedDay">
                    <div class="mb-4 flex flex-wrap gap-2 text-sm">
                        <Tag color="success">開放 {{ selectedDay.summary?.open ?? 0 }}</Tag>
                        <Tag color="processing">
                            已預約 {{ selectedDay.summary?.booked ?? 0 }}
                        </Tag>
                        <Tag>不可排 {{ selectedDay.summary?.unavailable ?? 0 }}</Tag>
                    </div>
                    <ul class="m-0 list-none p-0 space-y-2">
                        <li
                            v-for="slot in selectedDay.slots"
                            :key="slot.starts_at"
                            class="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2.5"
                        >
                            <div class="flex items-center justify-between gap-2">
                                <span class="font-medium text-slate-800">{{ slot.label }}</span>
                                <Space :size="4">
                                    <Tag
                                        v-if="slot.admin_scheduled"
                                        color="orange"
                                        class="!m-0 min-w-[1.25rem] !px-1 text-center !text-[11px]"
                                    >
                                        代
                                    </Tag>
                                    <Tag :color="kindTagColor(slot.kind)">
                                        {{ kindLabel(slot.kind) }}
                                    </Tag>
                                </Space>
                            </div>
                            <p
                                v-if="slot.kind === 'booked' && slot.student_name"
                                class="mb-0 mt-1 text-sm text-slate-600"
                            >
                                學員：{{ slot.student_name }}
                                <span v-if="slot.mode" class="text-slate-400">
                                    （{{ modeLabel(slot.mode) }}）
                                </span>
                            </p>
                            <p
                                v-if="slot.kind === 'booked' && slot.version_id"
                                class="mb-0 mt-1 text-xs text-slate-500"
                            >
                                版本：
                                <RouterLink
                                    :to="versionEditorTo(slot.version_id)"
                                    class="text-emerald-700 hover:underline"
                                >
                                    {{ versionLabel(slot) }}
                                </RouterLink>
                            </p>
                        </li>
                    </ul>
                </template>
            </Drawer>
        </template>
    </div>
</template>

<style scoped>
.consultant-schedule {
    max-width: 1100px;
}

.schedule-page-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 16px;
}

.schedule-page-bar__back.ant-btn {
    padding-inline: 6px;
    color: #6b5428;
    font-weight: 600;
}

.schedule-page-bar__back.ant-btn:hover {
    color: #8b6914;
    background: #faf6ee !important;
}

.schedule-page-bar__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: 0.01em;
}

.schedule-month-nav {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 2px 4px 16px;
}

.schedule-month-nav__pager {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.schedule-month-nav__arrow.ant-btn {
    width: 32px;
    height: 32px;
    color: #6b5428;
}

.schedule-month-nav__arrow.ant-btn:hover {
    color: #8b6914;
    background: #faf6ee !important;
}

.schedule-month-nav__label {
    min-width: 7.5rem;
    text-align: center;
    font-size: 15px;
    font-weight: 650;
    color: #1f2937;
}

.schedule-month-nav__today.ant-btn {
    height: 30px;
    padding-inline: 14px;
    border-radius: 999px;
    border-color: #d9c89a;
    color: #6b5428;
    font-weight: 600;
    background: #faf6ee;
    box-shadow: none;
}

.schedule-month-nav__today.ant-btn:not(:disabled):hover {
    border-color: #c4a35a;
    color: #8b6914;
    background: #f3ead6 !important;
}

.schedule-month-nav__today.is-current.ant-btn,
.schedule-month-nav__today.ant-btn:disabled {
    border-color: #c4a35a;
    color: #fff;
    background: linear-gradient(180deg, #c4a35a 0%, #a8894a 100%);
    opacity: 1;
}

.schedule-calendar__head,
.schedule-calendar__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
}

.schedule-calendar__weekday {
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    padding: 4px 0;
}

.schedule-calendar__weekday.is-weekend {
    color: #94a3b8;
}

.schedule-calendar__cell {
    min-height: 88px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: #fff;
}

.schedule-calendar__cell--empty {
    border-color: transparent;
    background: transparent;
}

.schedule-calendar__cell--day {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 6px 8px;
    cursor: pointer;
    text-align: left;
    transition:
        border-color 0.15s,
        box-shadow 0.15s;
}

.schedule-calendar__cell--day:hover:not(:disabled) {
    border-color: #c4a35a;
    box-shadow: 0 2px 8px rgba(196, 163, 90, 0.18);
}

.schedule-calendar__cell--day.is-blocked {
    opacity: 0.45;
    cursor: not-allowed;
    background: #f8fafc;
}

.schedule-calendar__cell--day.is-blocked:hover {
    border-color: #e2e8f0;
    box-shadow: none;
}

.schedule-calendar__cell--day.is-today {
    border-color: #c4a35a;
    background: #faf6ee;
}

.schedule-calendar__date {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
}

.schedule-calendar__pills {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 4px;
    flex: 1;
}

.pill {
    font-size: 10px;
    line-height: 1.3;
    padding: 2px 4px;
    border-radius: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pill--open {
    background: #d1fae5;
    color: #047857;
}

.pill--booked {
    background: #dbeafe;
    color: #1d4ed8;
}

.pill--off {
    background: #f1f5f9;
    color: #64748b;
}
</style>
